import React, { useState, useEffect } from 'react';
import { Download } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import BrochureModal from './BrochureModal';

const API_URL = '';

const DEFAULTS = {
  video_url: "/hero-video-ogd.mp4",
  fallback_video_url: "https://videos.pexels.com/video-files/4065924/4065924-uhd_2560_1440_24fps.mp4",
  title_fr: "Là où le territoire devient destination",
  title_en: "Where territory becomes destination",
  subtitle_fr: "Chez One Green Dev, nous révélons le potentiel des lieux.",
  subtitle_en: "At One Green Dev, we reveal the potential of places."
};

const HeroVideo = () => {
  const { language } = useLanguage();
  const [isVisible, setIsVisible] = useState(false);
  const [content, setContent] = useState(DEFAULTS);
  const [isBrochureOpen, setIsBrochureOpen] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 100);
    fetch(`${API_URL}/api/public/homepage/hero`)
      .then(r => r.ok ? r.json() : DEFAULTS)
      .then(d => setContent(d))
      .catch(() => {});
    return () => clearTimeout(timer);
  }, []);

  const scrollToNext = () => {
    document.getElementById('manifeste')?.scrollIntoView({ behavior: 'smooth' });
  };

  const title = language === 'fr' ? content.title_fr : content.title_en;
  const subtitle = language === 'fr' ? content.subtitle_fr : content.subtitle_en;

  return (
    <section className="relative h-screen w-full overflow-hidden" data-testid="hero-section">
      {/* Video Background */}
      <div className="absolute inset-0">
        <video
          autoPlay
          muted
          loop
          playsInline
          className="w-full h-full object-cover scale-105"
          style={{ filter: 'brightness(0.72)' }}
        >
          <source src={content.video_url} type="video/mp4" />
          <source src={content.fallback_video_url} type="video/mp4" />
        </video>
        
        {/* Gradient overlays */}
        <div className="absolute inset-0 bg-gradient-to-b from-emerald-950/25 via-transparent to-emerald-950/60" />
        <div className="absolute inset-0 bg-gradient-to-r from-emerald-950/30 via-transparent to-emerald-950/20" />
      </div>

      {/* Content */}
      <div className="relative z-10 h-full flex flex-col justify-center items-center px-6 text-center">
        {/* Main headline */}
        <div className={`transform transition-all duration-1000 delay-300 ${
          isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
        }`}>
          <h1 data-testid="hero-title" className="font-display text-3xl sm:text-4xl md:text-4xl lg:text-5xl xl:text-6xl text-white tracking-tight max-w-4xl" style={{ lineHeight: 1.15 }}>
            {title}
          </h1>
        </div>

        {/* Subtitle */}
        <div className={`mt-5 sm:mt-6 transform transition-all duration-1000 delay-500 ${
          isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
        }`}>
          <p data-testid="hero-subtitle" className="font-sans text-base sm:text-lg md:text-xl text-white/75 max-w-2xl leading-relaxed">
            {subtitle}
          </p>
        </div>

        {/* CTA Buttons */}
        <div className={`mt-8 flex flex-col sm:flex-row items-center gap-4 transform transition-all duration-1000 delay-700 ${
          isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
        }`}>
          <button
            onClick={scrollToNext}
            className="group px-8 py-3.5 bg-white text-emerald-900 text-sm font-medium uppercase tracking-wider rounded-sm transition-all duration-300 hover:bg-emerald-50 hover:shadow-lg"
            style={{ fontFamily: "'Montserrat', sans-serif" }}
            data-testid="hero-cta-discover"
          >
            {language === 'fr' ? 'Découvrir' : 'Discover'}
          </button>
          <button
            onClick={() => setIsBrochureOpen(true)}
            className="group flex items-center gap-2.5 px-8 py-3.5 border border-white/40 text-white text-sm font-medium uppercase tracking-wider rounded-sm transition-all duration-300 hover:bg-white/10 hover:border-white/70"
            style={{ fontFamily: "'Montserrat', sans-serif" }}
            data-testid="hero-cta-brochure"
          >
            <Download className="w-4 h-4" />
            {language === 'fr' ? 'Recevoir la brochure' : 'Get brochure'}
          </button>
        </div>
      </div>

      {/* Brochure Modal */}
      <BrochureModal isOpen={isBrochureOpen} onClose={() => setIsBrochureOpen(false)} />
    </section>
  );
};

export default HeroVideo;
